<script setup>
  import { computed, useAttrs } from "vue";

  const attrs = useAttrs();
  const props = defineProps({
    variant: {
      type: String,
      default: "fill", // fill | outlined | text | default
    },
    color: {
      type: String,
      default: "primary", // primary | gray
    },
  });
  const variantClass = computed(
    () => `btn-${props.variant} btn-${props.color}`,
  );
  const filteredAttrs = computed(() => {
    const { class: _c, style: _s, ...rest } = attrs;
    return rest;
  });
</script>

<template>
  <n-button
    class="ui-button"
    :class="variantClass"
    v-bind="filteredAttrs"
    style="
      --n-border: none;
      --n-border-hover: none;
      --n-border-pressed: none;
      --n-border-focus: none;
      --n-border-disabled: none;
    "
  >
    <slot />
    <template v-for="(_, name) in $slots" v-slot:[name]>
      <slot :name="name" />
    </template>
  </n-button>
</template>

<style lang="scss" scoped>
  .n-button.ui-button {
    background: var(--btn-bg-color, none);
    border: var(--btn-border, none);
    border-radius: var(--btn-border-radius, 0.625rem);
    color: var(--btn-text-color, var(--smoe-gray-800));
    transition: 0.2s ease-in-out;
    min-width: 80px;
    font-weight: var(--btn-font-weight, 600);
    &.n-button--small-type {
      min-width: 1px;
      --btn-border-radius: 0.5rem;
    }
    &.n-button--tiny-type {
      min-width: 1px;
      --btn-border-radius: 0.375rem;
    }
    &:hover:not(.btn-text):not(.btn-default) {
      background: var(--btn-hover-bg-color, var(--btn-bg-color));
      border: var(--btn-hover-border, var(--btn-border));
      color: var(--btn-hover-text-color, var(--btn-text-color));
    }
    &:active:not(.btn-text):not(.btn-default) {
      background: var(--btn-active-bg-color, var(--btn-bg-color));
      border: var(--btn-active-border, var(--btn-border));
      color: var(--btn-active-text-color, var(--btn-text-color));
    }
    &:disabled {
      opacity: 0.5;
    }

    &.btn-fill {
      &.btn-primary {
        --btn-bg-color: var(--smoe-primary-500);
        --btn-text-color: var(--smoe-white);
        --btn-hover-bg-color: var(--smoe-primary-600);
        --btn-active-bg-color: var(--smoe-primary-700);
      }
      &.btn-gray {
        --btn-bg-color: var(--smoe-gray-500);
        --btn-text-color: var(--smoe-white);
        --btn-hover-bg-color: var(--smoe-gray-600);
        --btn-active-bg-color: var(--smoe-gray-700);
      }
    }
    &.btn-outlined {
      &.btn-primary {
        --btn-border: 1px solid var(--smoe-primary-500);
        --btn-bg-color: var(--smoe-white);
        --btn-text-color: var(--smoe-primary-500);
        --btn-hover-bg-color: var(--smoe-primary-100);
        --btn-active-bg-color: var(--smoe-primary-200);
      }
      &.btn-gray {
        --btn-border: 1px solid var(--smoe-border);
        --btn-bg-color: var(--smoe-white);
        --btn-text-color: var(--smoe-gray-800);
        --btn-hover-bg-color: var(--smoe-gray-50);
        --btn-active-bg-color: var(--smoe-gray-100);
      }
    }
    &.btn-text {
      --btn-text-color: var(--smoe-gray-800);
      height: auto !important;
      text-decoration: underline;
      padding: 0;
      min-width: 1px !important;
      &:hover {
        box-shadow: none;
      }
      &:focus,
      &:active {
        box-shadow: none;
      }
    }
    &.btn-default {
      min-width: 1px;
      padding: 0;
    }
  }
</style>
